import { ReactElement, ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import {
  Breadcrumbs,
  Error,
  Prompt,
  Success,
  Tooltip,
  Typography,
  SnackbarStatus,
  useSnackbar,
  EllipsisMenu,
  ExpandToggle,
  ArrowDownIcon,
  Bin,
  ArrowUpIcon,
  EditIcon,
  AddPriceBandIcon,
  PriceBandIcon,
  Chip,
  Plus,
  SoftButton,
  ChannelIcon,
  IOption,
} from '@weareopenr/component-library';
import classnames from 'classnames/bind';
import { useQueryClient } from '@tanstack/react-query';
import styles from './MenuSection.module.scss';
import MenuItemCardSkeleton from 'src/components/MenuItemCardSkeleton';
import { moveSection } from 'src/api/moveSection/moveSection';
import { IMenuItem, ISection } from 'src/types/menu.types';
import { deleteSection } from 'src/api/deleteSection/deleteSection';
import { getHierarchyNames } from 'src/helpers';
import { getPromptText } from './utilities/getPromptText';
import { ICategoryWithChildren } from 'src/components/MenuSectionItems/utilities';
import { QueryKeys } from 'src/hooks/QueryKeys';
import {
  getSectionUnavailability,
  isSectionRestricted,
  isSectionVenueRestricted,
} from 'src/helpers/sectionAvailability/sectionAvailability';
import { useMenuSectionDragAndDrop } from './useMenuSectionDragAndDrop';
import { useSectionExpansion } from './useSectionExpansion';
import MenuSectionMeta from './MenuSectionMeta';
import MenuSectionItemCard from './MenuSectionItemCard';
import MenuSectionCardGrid from './MenuSectionCardGrid';
import { StatusChip } from 'src/components/StatusChip/StatusChip';
import { NO_AVAILABILITY_MESSAGE } from './constants';

export interface ISectionTrayTarget {
  parentSectionId?: string | null;
  section?: ICategoryWithChildren;
}

export interface ISiblingDisplayOrder {
  up?: number;
  down?: number;
}

export interface IPendingAddition {
  sectionId: string | null;
  count: number;
}

export interface IMenuSectionProps {
  menuId: string;
  section: ICategoryWithChildren;
  handleSectionModal?: (options?: ISectionTrayTarget) => void;
  handleManageAvailabilityModal?: (section: ICategoryWithChildren) => void;
  onClickNewItem?: (sectionId?: string) => void;
  displayOrder?: ISiblingDisplayOrder;
  isAssignedToMealtime: boolean | undefined;
  tooltipPosition: 'top' | 'bottom';
  sections: ISection[];
  children?: ReactNode;
  isAddProductsHidden?: boolean;
  pendingAddition?: IPendingAddition;
  // 'view' is a read-only section; 'menu' is a whole menu shown read-only as a top-level section
  // (within a menu set), with its own sections passed in as children.
  variant?: 'default' | 'unassigned' | 'view' | 'menu';
  scrollToSectionId?: string | null;
  onScrolledIntoView?: () => void;
  previewedItemId?: string | null;
  isDragSuspended?: boolean;
  committedItemCounts?: Map<string, number>;
  menuRanges?: { range_id: string; name: string }[];
  menuOptions?: IOption[];
}
const cx = classnames.bind(styles);
const MenuSection = ({
  menuId,
  handleSectionModal,
  handleManageAvailabilityModal,
  onClickNewItem,
  section,
  displayOrder,
  isAssignedToMealtime,
  tooltipPosition,
  sections,
  children,
  isAddProductsHidden,
  pendingAddition,
  variant = 'default',
  scrollToSectionId,
  onScrolledIntoView,
  previewedItemId,
  isDragSuspended,
  committedItemCounts,
  menuRanges = [],
  menuOptions,
}: IMenuSectionProps): ReactElement => {
  const isUnassigned = variant === 'unassigned';
  const isMenu = variant === 'menu';
  const isViewOnly = variant === 'view' || isMenu;
  const { sectionId, name, description, items, parentSectionId, children: subsections } = section;
  const sectionCardRef = useRef<HTMLDivElement>(null);
  const queryClient = useQueryClient();
  const isChannelRestricted = isSectionRestricted(section);
  const isVenueRestricted = isSectionVenueRestricted(section);
  const isRestricted = isChannelRestricted || isVenueRestricted;
  const { isChannelsUnavailable, isVenuesUnavailable, isSectionUnavailable } = getSectionUnavailability(
    section,
    sections,
  );
  const isMenuSection = !isUnassigned && !isMenu;
  const channels = isChannelRestricted ? section.channels ?? [] : [];
  const venueCount = isVenueRestricted ? (section.venues ?? []).length : undefined;
  const {
    canReceiveDrops,
    isDragEnabled,
    hasDuplicateItemIds,
    previewedCardId,
    isDropTarget,
    sortableItemIds,
    isItemDraggable,
    setDropZoneRef,
  } = useMenuSectionDragAndDrop({
    section,
    sections,
    isUnassigned,
    isViewOnly,
    previewedItemId,
    isDragSuspended,
  });
  const { showSnackbar, hideSnackbar } = useSnackbar();
  const [isDeleteLoading, setIsDeleteLoading] = useState(false);
  const [isRemovePromptVisible, setIsRemovePromptVisible] = useState<boolean>(false);
  const { isExpanded, toggleExpanded } = useSectionExpansion({ isDropTarget });
  const removePromptTitle = `Are you sure you want to delete "${name}" ${
    parentSectionId ? 'sub-section' : 'menu section'
  }?`;
  const removeSectionPromptSubTitle = getPromptText(name, items, subsections);
  const categoryBreadcrumbs = useMemo(
    () =>
      getHierarchyNames(section, sections, { id: 'sectionId', parent_id: 'parentSectionId' }).map((name) => ({
        label: name,
      })),
    [section, sections],
  );

  useEffect(() => {
    if (scrollToSectionId && scrollToSectionId === sectionId) {
      sectionCardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      onScrolledIntoView?.();
    }
  }, [scrollToSectionId, sectionId, onScrolledIntoView]);

  const handleDelete = async () => {
    setIsDeleteLoading(true);
    try {
      await deleteSection(menuId, sectionId);
      await queryClient.invalidateQueries({ queryKey: [QueryKeys.menu, menuId] });
    } catch (e) {
      showSnackbar({
        status: SnackbarStatus.ERROR,
        message: 'We were unable to delete, please try again!',
      });
      setIsDeleteLoading(false);
    }
  };

  const reorderSection = async (newDisplayOrder: number) => {
    try {
      showSnackbar({
        status: SnackbarStatus.PROGRESS,
        message: 'Moving menu section',
      });
      await moveSection({ menuId, sectionId, newDisplayOrder });
      await queryClient.invalidateQueries({ queryKey: [QueryKeys.menu, menuId] });
      hideSnackbar();
    } catch (error) {
      showSnackbar({
        status: SnackbarStatus.ERROR,
        message: 'We have been unable to complete your request',
      });
    }
  };

  const pendingCount = pendingAddition?.sectionId === (sectionId || null) ? pendingAddition.count : 0;

  const renderItemCard = (item: IMenuItem, index: number) => {
    // itemId is the stable key wherever it is unique, which keeps reorder animations intact.
    // A section that repeats an item has no stable per-card identity, so fall back to position -
    // dragging is already disabled there, so the remount cost of a positional key does not apply.
    const key = hasDuplicateItemIds ? `${item.itemId}-${index}` : item.itemId;

    return (
      <MenuSectionItemCard
        key={key}
        item={item}
        menuId={menuId}
        sectionId={sectionId}
        displayOrderLeft={items[index - 1]?.displayOrder}
        displayOrderRight={items[index + 1]?.displayOrder}
        isPreviewed={item.itemId === previewedCardId}
        isDraggable={isItemDraggable(item.itemId)}
        isViewOnly={isViewOnly}
      />
    );
  };

  const itemCards = [
    ...items.map((item, index) => renderItemCard(item, index)),
    ...Array.from({ length: pendingCount }, (_, index) => <MenuItemCardSkeleton key={`pending-${index}`} />),
    !isAddProductsHidden && !isViewOnly && (
      <SoftButton
        colour="blueberry"
        icon={Plus}
        size="square"
        className={styles['add-product']}
        onClick={() => onClickNewItem?.(sectionId)}
        key="add"
      >
        Add products
      </SoftButton>
    ),
  ];

  const ellipsisMenu = (
    <EllipsisMenu
      disabled={isAddProductsHidden}
      options={[
        {
          id: 0,
          name: 'Edit',
          icon: EditIcon,
          onOptionClick: () => handleSectionModal?.({ section }),
          dataTestId: `rename-section-${name}`,
        },
        {
          id: 'manage-availability',
          name: 'Manage availability',
          icon: ChannelIcon,
          onOptionClick: () => handleManageAvailabilityModal?.(section),
          dataTestId: `manage-availability-section-${name}`,
        },
        {
          id: 1,
          name: 'Move up',
          icon: ArrowUpIcon,
          onOptionClick: () => reorderSection(displayOrder?.up),
          isDisabled: displayOrder?.up === undefined,
          dataTestId: `move-up-section-${name}`,
        },
        {
          id: 2,
          name: 'Move down',
          icon: ArrowDownIcon,
          onOptionClick: () => reorderSection(displayOrder?.down),
          isDisabled: displayOrder?.down === undefined,
          dataTestId: `move-down-section-${name}`,
        },
        {
          id: 'd-1',
          isDivider: true,
        },
        {
          id: 'add-category',
          name: 'Add section',
          icon: AddPriceBandIcon,
          onOptionClick: () => handleSectionModal?.({ parentSectionId: null }),
        },
        {
          id: 'add-subcategory',
          name: 'Add subsection',
          icon: PriceBandIcon,
          onOptionClick: () => handleSectionModal?.({ parentSectionId: sectionId }),
        },
        {
          id: 'd-2',
          isDivider: true,
        },
        {
          id: 3,
          name: 'Delete',
          icon: Bin,
          onOptionClick: () => setIsRemovePromptVisible(true),
          hoverColour: 'tomato',
          dataTestId: `delete-section-${name}`,
        },
      ]}
      variant="ghost"
    />
  );

  // Counted from the cached menu, so a hover preview does not move the number before the card is
  // dropped. The empty check stays on the live list: an empty section counted as empty would be
  // forced collapsed, leaving a card hovered over it nowhere to preview into.
  const productCount = committedItemCounts?.get(sectionId) ?? items.length;
  const subsectionCount = subsections.length;
  const isEmptyWithoutAddButton = items.length === 0 && pendingCount === 0 && subsectionCount === 0;
  const isExpandToggleDisabled = (isAddProductsHidden || isViewOnly) && isEmptyWithoutAddButton;
  const isSectionExpanded = isExpandToggleDisabled ? false : isExpanded;

  return (
    <div
      ref={sectionCardRef}
      className={cx('section-card', {
        'section-card-unassigned': isUnassigned,
        'section-card-view': variant === 'view',
        'section-card-menu': isMenu,
        'section-card-top-level': !isUnassigned && !parentSectionId,
        'section-card-unavailable': isMenuSection && isSectionUnavailable,
        'section-card-drop-target': isDropTarget,
      })}
      data-testid={`${name}-section-divider`}
    >
      {!isUnassigned && !isViewOnly && (
        <Prompt
          promptHeading={removePromptTitle}
          closePrompt={() => setIsRemovePromptVisible(false)}
          open={isRemovePromptVisible}
          size="small"
          backButton={true}
          backButtonText="Back"
          buttonText="Delete"
          buttonColour="tomato"
          buttonType="button"
          buttonHandler={handleDelete}
          isSubmitting={isDeleteLoading}
        >
          <Typography>{removeSectionPromptSubTitle}</Typography>
        </Prompt>
      )}
      <div
        className={styles['drop-zone']}
        ref={canReceiveDrops ? setDropZoneRef : undefined}
        data-testid={canReceiveDrops ? `section-drop-zone-${sectionId}` : undefined}
      >
        <div className={styles['header']}>
          <div className={styles['header-main']}>
            <div className={styles['title-row']}>
              {isUnassigned || isMenu ? (
                <Typography size="large" weight="bold">
                  {name}
                </Typography>
              ) : (
                <Breadcrumbs breadcrumbs={categoryBreadcrumbs} maxLevel={2} size="large" boldLast />
              )}
              <ExpandToggle
                isExpanded={isSectionExpanded}
                onClick={() => !isExpandToggleDisabled && toggleExpanded()}
                size="small"
                className={cx('expand-toggle', { 'expand-toggle-disabled': isExpandToggleDisabled })}
              />
              {isAssignedToMealtime !== undefined && !parentSectionId && (
                <Tooltip
                  text={
                    isAssignedToMealtime
                      ? 'This menu section has been assigned to a mealtime and is ready to be published.'
                      : 'This menu section is not assigned to a mealtime, and will not be published when using mealtimes.'
                  }
                  position={tooltipPosition}
                >
                  {isAssignedToMealtime ? (
                    <Success className={styles['kale']} />
                  ) : (
                    <Error className={styles['tomato']} />
                  )}
                </Tooltip>
              )}
            </div>
            {description && <Typography className={styles['description']}>{description}</Typography>}
            {isMenu ? (
              <ul className={styles['menu-meta']}>
                <li>
                  <Typography size="xsmall" weight="bold" className={styles['caviar']}>
                    Menu Sections -
                  </Typography>
                  <Typography size="xsmall" className={styles['caviar']}>
                    {subsectionCount}
                  </Typography>
                </li>
                {menuRanges.length > 0 && (
                  <li>
                    <Typography size="xsmall" weight="bold" className={styles['caviar']}>
                      Ranges -
                    </Typography>
                    {menuRanges.map((range) => (
                      <Chip key={range.range_id} label={range.name} icon="priceBand" backgroundColour="grape" />
                    ))}
                  </li>
                )}
              </ul>
            ) : (
              <MenuSectionMeta
                productCount={productCount}
                subsectionCount={subsectionCount}
                channels={channels}
                isChannelsUnavailable={isChannelsUnavailable}
                venueCount={venueCount}
                isVenuesUnavailable={isVenuesUnavailable}
              />
            )}
          </div>
          <div className={styles['header-actions']}>
            {isUnassigned && <Chip label="Excluded" backgroundColour="tomato" />}
            {isMenu && <StatusChip entity="menu-set" status="VIEW_ONLY" />}
            {isMenu && menuOptions && <EllipsisMenu options={menuOptions} variant="ghost" />}
            {isMenuSection && (
              <>
                {isSectionUnavailable && (
                  <Tooltip text={NO_AVAILABILITY_MESSAGE}>
                    <Chip label="No Availability" backgroundColour="tomato" icon="attention" />
                  </Tooltip>
                )}
                {!isSectionUnavailable && isRestricted && (
                  <Chip label="Partial Availability" backgroundColour="grape" />
                )}
                {!isViewOnly && ellipsisMenu}
              </>
            )}
          </div>
        </div>

        {isSectionExpanded && !isMenu && (
          <MenuSectionCardGrid
            sectionId={sectionId}
            isDragEnabled={isDragEnabled}
            sortableItemIds={sortableItemIds}
            cards={itemCards}
          />
        )}
      </div>
      {isSectionExpanded && children}
    </div>
  );
};

export default MenuSection;
